// src/services/mappers/accountMapper.ts
import type {
  VirtualCashAccountDto,
  Currency as ApiCurrency,
  RecentTransactionDto,
  PaginatedListOfRecentTransactionDto,
  WithdrawalAccountDto,
  RequestWithdrawalCommand,
  DepositDetailsDto,
} from '@generated/api';
import {
  VirtualCashAccount,
  AccountSummary,
  Currency,
  Transaction,
  PaginatedTransactions,
  WithdrawalAccount,
  WithdrawalRequest,
  DepositDetails,
} from '@typeDefs/domain/entities';

const mapCurrencyFromApi = (apiCurrency?: ApiCurrency): Currency => {
  // Map from API enum to domain enum
  // Adjust based on your actual API currency values
  switch (apiCurrency) {
    case 0: // Assuming Value0 represents USD
      return Currency.USD;
    default:
      return Currency.USD;
  }
};

const mapCurrencyToApi = (currency: Currency): ApiCurrency => {
  switch (currency) {
    case Currency.USD:
      return 0; // Assuming Value0 represents USD
    default:
      return 0;
  }
};

const mapTransactionFromApi = (apiData: RecentTransactionDto): Transaction => {
  // Parse and format description based on transaction type
  let formattedDescription = apiData.description || '';

  if (formattedDescription === 'ClientCreatesOrder' && apiData.isin) {
    formattedDescription = `Request T-Bill ${apiData.isin}`;
  } else if (formattedDescription === 'DepositByClient') {
    formattedDescription = 'Money Deposited';
  } else if (formattedDescription === 'AssetMaturitySettlement') {
    formattedDescription = `T-Bill ${apiData.isin} Maturity Settlement`;
  } else if (formattedDescription === 'FeePayment') {
    formattedDescription = 'Service Charge';
  } else if (formattedDescription === 'OrderCancelled') {
    formattedDescription = `T-Bill ${apiData.isin} Order Cancellation Settlement`;
  } else if (formattedDescription === 'DCPartnerPurchasesAsset') {
    // #169 smoke F2 — ONE journal entry type, TWO opposite money events. This is the reservation
    // adjustment posted when a dealer executes a buy at a price other than the one the client
    // reserved, and it fires in both directions:
    //
    //   * isDebit TRUE  → Cash DR → the unused part of the reservation is coming BACK to the client
    //                     (dealer filled below the reserved amount). "Reimbursement" is correct.
    //   * isDebit FALSE → Cash CR → the dealer filled ABOVE the reserved amount and the client is
    //                     charged the difference. "Reimbursement" states the exact opposite.
    //
    // This branch previously read `description` alone, so both collapsed onto the reimbursement
    // string — telling a client who had just been charged extra that they had been paid back. Both
    // directions were observed live on a single order (Cash DR £1.65 and Cash CR £431.62).
    //
    // `isDebit` is the Cash ledger line's own DR/CR flag, passed through untouched by the server
    // (client-api's ClientAccountService projects `IsDebit = line.IsDebit` off the client's Cash
    // account), so it is the authoritative direction signal and needs no local inference.
    //
    // The charged direction follows the ISIN-bearing pattern of its siblings above; the credit-back
    // keeps its existing copy verbatim, since that copy was already right and clients have seen it.
    //
    // The ISIN is guarded because it is nullable on the wire (RecentTransactionDto.ISIN is joined via
    // DefaultIfEmpty, so a row whose JournalEntry carries no OrderId yields null). Unguarded template
    // interpolation renders the literal 'T-Bill null Order Balance Due' on a client-facing money row.
    // The sibling branches above share this latent flaw and are deliberately left alone — out of
    // scope here — but new copy should not add another instance of it.
    formattedDescription = apiData.isDebit
      ? 'Reimbursement of Order Complete'
      : apiData.isin
        ? `T-Bill ${apiData.isin} Order Balance Due`
        : 'Order Balance Due';
  } else if (formattedDescription === 'SaleSettlement') {
    formattedDescription = `T-Bill ${apiData.isin} Sale Settlement`;
  } else if (formattedDescription === 'WithdrawalRequest') {
    formattedDescription = 'Money Withdrawal';
  }

  return {
    amount: apiData.amount || 0,
    isDebit: apiData.isDebit || false,
    description: formattedDescription,
    transactionDate: apiData.transactionDate ? new Date(apiData.transactionDate) : new Date(),
    isin: apiData.isin || undefined,
  };
};

/**
 * #160b (feature preservoir-ai#117) — the two cash figures, derived once.
 *
 * `availableBalance` on the wire is NET: gross ledger cash minus management fees
 * accrued but not yet extracted. Clients must SEE the gross figure (a net display
 * appears to erode daily with no money movement) but may only SPEND the net one.
 *
 * Both are resolved here, exactly once, under names that cannot be confused —
 * every display binding reads `grossAvailableBalance`, every spend cap reads
 * `availableBalance`.
 *
 * Every money field on `VirtualCashAccountDto` is generated as `any`, so a
 * misspelled read compiles clean and yields `undefined` → gross would silently
 * collapse onto net. `accountMapper.test.ts` is the only pin on these names.
 */
const resolveCashFigures = (
  apiData: VirtualCashAccountDto,
): { availableBalance: number; feesPayableBalance: number; grossAvailableBalance: number } => {
  const availableBalance: number = apiData.availableBalance || 0;
  const feesPayableBalance: number = apiData.feesPayableBalance || 0;

  return {
    availableBalance,
    feesPayableBalance,
    grossAvailableBalance: availableBalance + feesPayableBalance,
  };
};

export const accountMapper = {
  fromApi: (apiData: VirtualCashAccountDto): VirtualCashAccount => ({
    accountNumber: apiData.accountNumber || '',
    accountName: apiData.accountName || '',
    currency: mapCurrencyFromApi(apiData.currency),
    totalBalance: apiData.totalBalance || 0,
    reservedBalance: apiData.reservedBalance || 0,
    ...resolveCashFigures(apiData),
    createdDate: apiData.createdDate ? new Date(apiData.createdDate) : new Date(),
  }),

  toApi: (domainData: VirtualCashAccount): VirtualCashAccountDto => ({
    accountNumber: domainData.accountNumber,
    accountName: domainData.accountName,
    currency: mapCurrencyToApi(domainData.currency),
    totalBalance: domainData.totalBalance,
    reservedBalance: domainData.reservedBalance,
    // Only the two SOURCE figures go back on the wire. `grossAvailableBalance` is
    // a client-side derivation and is deliberately not sent — posting a derived
    // field back invites it drifting out of step with its inputs.
    availableBalance: domainData.availableBalance,
    feesPayableBalance: domainData.feesPayableBalance,
    createdDate: domainData.createdDate.toISOString(),
  }),

  summaryFromApi: (apiData: VirtualCashAccountDto): AccountSummary => ({
    accountNumber: apiData.accountNumber || '',
    ...resolveCashFigures(apiData),
    currency: mapCurrencyFromApi(apiData.currency),
  }),

  transactionFromApi: mapTransactionFromApi,

  paginatedTransactionsFromApi: (
    apiData: PaginatedListOfRecentTransactionDto,
  ): PaginatedTransactions => ({
    items: (apiData.items || []).map(mapTransactionFromApi),
    pageNumber: apiData.pageNumber || 1,
    totalPages: apiData.totalPages || 1,
    totalCount: apiData.totalCount || 0,
    hasPreviousPage: apiData.hasPreviousPage || false,
    hasNextPage: apiData.hasNextPage || false,
  }),

  // Withdrawal Account mappings
  withdrawalAccountFromDto: (dto: WithdrawalAccountDto): WithdrawalAccount => ({
    id: dto.id ?? '',
    accountName: dto.accountName ?? '',
    maskedAccountNumber: dto.maskedAccountNumber ?? '',
    maskedIBAN: dto.maskedIBAN ?? '',
    sortCode: dto.sortCode ?? '',
    bankAddress: dto.bankAddress ?? '',
    currency: (dto.currency as unknown as Currency) ?? Currency.GBP,
    isVerified: dto.isVerified ?? false,
  }),

  withdrawalAccountsFromDto: (dtos: WithdrawalAccountDto[]): WithdrawalAccount[] => {
    return dtos.map(accountMapper.withdrawalAccountFromDto);
  },

  // Withdrawal Request mappings
  withdrawalRequestToCommand: (request: WithdrawalRequest): RequestWithdrawalCommand => {
    // Validate required fields
    if (!request.corporateClientId || request.corporateClientId.trim() === '') {
      throw new Error('Corporate client ID is required');
    }
    if (!request.withdrawalBankAccountId || request.withdrawalBankAccountId.trim() === '') {
      throw new Error('Withdrawal bank account ID is required');
    }
    if (!request.amount || request.amount <= 0) {
      throw new Error('Amount must be greater than 0');
    }

    return {
      corporateClientId: request.corporateClientId,
      withdrawalBankAccountId: request.withdrawalBankAccountId,
      amount: request.amount,
    };
  },

  withdrawalResponseFromApi: (): boolean => {
    // API returns void, so we return true to indicate success
    return true;
  },

  // Deposit Details mappings
  depositDetailsFromDto: (dto: DepositDetailsDto): DepositDetails => ({
    accountName: dto.accountName ?? '',
    accountNumber: dto.accountNumber ?? '',
    sortCode: dto.sortCode ?? '',
    bankAddress: dto.bankAddress ?? '',
    referenceNumber: dto.referenceNumber ?? '',
    currency: dto.currency ?? 'GBP',
  }),
};
